import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { User, Role } from '../../types';
import { UserPlus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
export const StaffManagement: React.FC = () => {
  const {
    users,
    addUser,
    updateUser,
    deleteUser,
    currentUser
  } = useData();
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    fullName: '',
    password: '',
    role: 'Reception' as Role
  });
  const roles: {
    value: Role;
    label: string;
  }[] = [{
    value: 'Reception',
    label: 'Reception'
  }, {
    value: 'Nursing',
    label: 'Nursing'
  }, {
    value: 'Clinician',
    label: 'Clinician'
  }, {
    value: 'Laboratory',
    label: 'Laboratory'
  }, {
    value: 'Radiology',
    label: 'Radiology'
  }, {
    value: 'Pharmacy',
    label: 'Pharmacy'
  }, {
    value: 'Pharmacy Manager',
    label: 'Pharmacy Manager'
  }, {
    value: 'Administrator',
    label: 'Administrator'
  }];
  const handleOpenAdd = () => {
    setEditingUser(null);
    setFormData({
      fullName: '',
      password: '',
      role: 'Reception'
    });
    setShowModal(true);
  };
  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      fullName: user.fullName,
      password: user.password || '',
      role: user.role
    });
    setShowModal(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    if (formData.password.length < 6 || !/\d/.test(formData.password) || !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      alert('Password must be at least 6 characters and include numbers and special characters.');
      return;
    }
    if (editingUser) {
      updateUser(editingUser.id, formData);
    } else {
      addUser(formData);
    }
    setShowModal(false);
  };
  const handleDelete = (id: string, role: string) => {
    if (role === 'Administrator') {
      alert('Administrator accounts cannot be deleted.');
      return;
    }
    if (id === currentUser?.id) {
      alert('You cannot delete your own account.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUser(id);
    }
  };
  const togglePasswordVisibility = (id: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  return <div className="space-y-6" data-id="element-853" data-mp="1slwp9cy0re1y">
      <div className="flex justify-between items-center" data-id="element-854" data-mp="1smzhbo72vmjg">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-855" data-mp="3d509pqzpf1ee">Staff Management</h1>
        <Button onClick={handleOpenAdd} data-id="element-856" data-mp="385dvbe853f40">
          <UserPlus className="w-4 h-4 mr-2" data-id="element-857" data-mp="384cj36fe0mkp" /> Add New Staff
        </Button>
      </div>

      <Card noPadding data-id="element-858" data-mp="3430t2zslpzbz">
        <div className="overflow-x-auto" data-id="element-859" data-mp="344c6zsfrgngt">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-860" data-mp="2z4piunye9mt8">
            <thead className="bg-gray-50" data-id="element-861" data-mp="2z6k4k4hhpiyd">
              <tr data-id="element-862" data-mp="3vuqen6x6l3k1">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-863" data-mp="3vv8aw3jufhry">
                  Name / Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-864" data-mp="3n2ogjfal3zcx">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-865" data-mp="3i323p6yo4l4n">
                  Password
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-866" data-mp="embfrd34rneo">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-867" data-mp="36ygbxd5zaix">
              {users.map(user => <tr key={user.id} data-id="element-868" data-mp="3lclb5kz0vf7x">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-869" data-mp="3ldmk9jwh9rn0">
                    <div className="text-sm font-medium text-gray-900" data-id="element-870" data-mp="3qacys2smwf6b">
                      {user.fullName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-871" data-mp="hxp7d36xm5kz">
                    <Badge variant={user.role === 'Administrator' ? 'danger' : 'default'} data-id="element-872" data-mp="lvmywco6d3rj">
                      {user.role}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-873" data-mp="10tnpcr6aanea">
                    <div className="flex items-center" data-id="element-874" data-mp="10s48ozgpuabz">
                      <span className="text-sm text-gray-500 font-mono mr-2" data-id="element-875" data-mp="15lyymkmwzlji">
                        {showPasswords[user.id] ? user.password : '••••••••'}
                      </span>
                      <button onClick={() => togglePasswordVisibility(user.id)} className="text-gray-400 hover:text-gray-600" data-id="element-876" data-mp="1ayxdipydf22c">
                        {showPasswords[user.id] ? <EyeOff className="w-4 h-4" data-id="element-877" data-mp="1jsiri72pofap" /> : <Eye className="w-4 h-4" data-id="element-878" data-mp="w150q5k7krru" />}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-879" data-mp="15zkivj19cqp7">
                    <button onClick={() => handleOpenEdit(user)} className="text-blue-600 hover:text-blue-900 mr-4" data-id="element-880" data-mp="10zeucbqabkbc">
                      <Edit2 className="w-4 h-4" data-id="element-881" data-mp="2cf3g4zrsydak" />
                    </button>
                    <button onClick={() => handleDelete(user.id, user.role)} className={`${user.role === 'Administrator' ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`} disabled={user.role === 'Administrator'} data-id="element-882" data-mp="2ch7nxsstnwg4">
                      <Trash2 className="w-4 h-4" data-id="element-883" data-mp="2yfbwd3rnqq2e" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>

      {showModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-884" data-mp="25ry7zu20z2tw">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-885" data-mp="2aoycw1n51zjf">
            <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-886" data-mp="2fh82fgkccxl6">
              {editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-887" data-mp="1hz7v9imasfik">
              <Input label="Full Name (Used as Username)" required value={formData.fullName} onChange={e => setFormData({
            ...formData,
            fullName: e.target.value
          })} data-id="element-888" data-mp="1mz41zpmdql1t" />
              <Select label="Role" options={roles} required value={formData.role} onChange={e => setFormData({
            ...formData,
            role: e.target.value as Role
          })} data-id="element-889" data-mp="15kjdve2gtf50" />
              <Input label="Password" type="text" required value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} placeholder="Min 6 chars, 1 number, 1 special char" data-id="element-890" data-mp="mta20y582ea9" />
              <p className="text-xs text-gray-500" data-id="element-891" data-mp="1wpoq5b7pqr05">
                Password must be minimum 6 characters and include numbers and
                special characters.
              </p>

              <div className="mt-6 flex justify-end space-x-3" data-id="element-892" data-mp="1mpxwtcuw2i1n">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} data-id="element-893" data-mp="1rpsm5bz0mzkl">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-894" data-mp="1ipmalvmvo9o2">
                  {editingUser ? 'Save Changes' : 'Add Staff'}
                </Button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};